import { createFileRoute } from "@tanstack/react-router";
import { BrandLogo } from "@/components/BrandLogo";
import { POSTER_PROPUESTA } from "@/features/propuesta/contenido";
import { PaginaPropuesta } from "@/features/propuesta/PaginaPropuesta";
import { leerPropuesta } from "@/server/propuestas.functions";

const SITIO =
  (import.meta.env.VITE_SITE_URL as string | undefined)?.replace(/\/+$/, "") ||
  "https://lasusiedad.com";

/**
 * Propuesta por enlace de una pareja. Se pinta en el servidor para que
 * WhatsApp vea la vista previa («La boda de Lola») sin ejecutar JavaScript.
 * `?vista=juan` la enseña sin pedir nombre y sin apuntar la visita.
 */
export const Route = createFileRoute("/propuesta/$token")({
  validateSearch: (search: Record<string, unknown>): { vista?: string } => ({
    vista: typeof search.vista === "string" ? search.vista : undefined,
  }),
  loader: async ({ params }) => {
    if (!/^[a-f0-9]{32}$/.test(params.token)) return { propuesta: null };
    return { propuesta: await leerPropuesta({ data: { token: params.token } }) };
  },
  head: ({ loaderData, params }) => {
    const propuesta = loaderData?.propuesta;
    const titulo = propuesta
      ? `La boda de ${propuesta.nombrePareja ?? propuesta.nombre} · SUSIEDAD`
      : "Propuesta · SUSIEDAD";
    const descripcion =
      "Vuestra propuesta personalizada: el sonido, la luz y el montaje, a vuestra medida.";
    const imagen = propuesta?.imagenVistaPrevia ?? `${SITIO}${POSTER_PROPUESTA}`;
    return {
      meta: [
        { title: titulo },
        { name: "description", content: descripcion },
        { name: "robots", content: "noindex,nofollow" },
        { property: "og:title", content: titulo },
        { property: "og:description", content: descripcion },
        { property: "og:image", content: imagen },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:url", content: `${SITIO}/propuesta/${params.token}` },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  component: PropuestaPage,
});

function PropuestaPage() {
  const { token } = Route.useParams();
  const { vista } = Route.useSearch();
  const { propuesta } = Route.useLoaderData();
  if (!propuesta) return <EnlaceNoValido />;
  return <PaginaPropuesta token={token} propuesta={propuesta} soloVer={vista === "juan"} />;
}

function EnlaceNoValido() {
  return (
    <main className="dark flex min-h-[100dvh] flex-col items-center justify-center gap-6 bg-onyx px-6 text-center text-bone">
      <BrandLogo variant="monogram" className="h-10 w-auto" />
      <h1 className="display-lg">Este enlace ya no está activo</h1>
      <p className="max-w-md text-bone/70">Escríbenos y te mandamos uno nuevo.</p>
      <a className="btn-primary min-h-12 px-6" href="https://wa.me/34633199708">
        Escribir por WhatsApp
      </a>
    </main>
  );
}
